/**
 * focusTrail — a long-lived record of the ONE event we're hunting: a click
 * that should have put the caret somewhere and didn't.
 *
 * WHY (Walter 2026-11-03). Twice in one day: *"the cursor wouldn't stick where
 * I put it in the to do list, instead it insisted on blinking in the reminders
 * list"*, and later *"clicking onto the due tile, instead the server lights
 * up, and the notes on the archive tile"*. No lock band on screen either time,
 * and a hard refresh cleared it both times — so it is live state in the tab,
 * not anything stored.
 *
 * Reading the code found nothing: no focus trap, no refocus loop, and every
 * remaining `.focus()` call in the app sits behind a search jump, a Tab press,
 * a modal opening or the take-over button. There is precedent for going the
 * other way — the #248 caret mark was diagnosed by a dev-only diagnostic
 * (343717f) that came out two hours later once the cause was known (c765cb4).
 * This is the same move for the focus bug, but it has to live on PROD: the
 * symptom has only ever appeared there, and it is gone after a refresh, so a
 * dev-only build would never see it.
 *
 * SELECTIVE, like shrinkLog: a click that lands the caret correctly — which is
 * nearly all of them — records nothing. Only the two shapes that could be the
 * bug are kept, so a hundred-odd entries span weeks instead of minutes:
 *
 *   1. `click-missed` — the click landed on something that takes a caret (a
 *      list editor, a textarea, an input, any contenteditable) and, once the
 *      browser had settled, the focus was NOT inside that element's tile.
 *      That is the symptom itself, with the place focus actually went.
 *   2. `focus-stolen` — focus moved on its own: no click and no key press
 *      behind it, away from something that held a caret. That is the thief, if
 *      there is one.
 *
 * It records WHERE focus went, never what is written there — element kinds,
 * list ids and flags only, no line text. Same rule as the sync log.
 *
 * LOG-ONLY. It installs two passive listeners and never moves focus, never
 * calls preventDefault, and never writes to a list.
 */

import { getWindowLabel } from './windowId';

const KEY = 'lists_focus_trail_v1';
const MAX_ENTRIES = 150;
// Entries are a few short strings each, so this is generous. Still far below
// the content trail's budget: localStorage is shared with the offline queue,
// which holds real unsaved text and must never be evicted for a diagnostic.
const MAX_BYTES = 150 * 1024;
// How long to wait after a pointerdown before asking where focus ended up.
// The browser's own focusing happens as the default action of the same event;
// React's state flush and any rAF work land just after. 150ms clears both
// without being long enough for a deliberate later move to look like a miss.
const SETTLE_MS = 150;
// A focus move further than this from the last click or key press had no user
// action behind it.
const UNPROMPTED_MS = 1000;

let installed = false;
let lastPointerAt = 0;
let lastKeyAt = 0;

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function write(entries) {
  let out = entries.slice(-MAX_ENTRIES);
  try {
    while (out.length > 1 && JSON.stringify(out).length > MAX_BYTES) out = out.slice(1);
    localStorage.setItem(KEY, JSON.stringify(out));
  } catch { /* quota or private mode — a diagnostic never throws */ }
}

/** The tile an element sits in, or null. */
function tileOf(el) {
  try { return el && el.closest ? el.closest('[data-list-id]') : null; } catch { return null; }
}

/**
 * A short, readable descriptor: what kind of thing it is and which tile it
 * belongs to. Kinds are deliberately coarse — the question is "which surface",
 * not "which div".
 */
function describe(el) {
  if (!el) return 'none';
  try {
    if (el === document.body) return 'body';
    const tag = (el.tagName || '').toLowerCase();
    let kind = tag;
    if (el.closest && el.closest('.cm-editor')) kind = 'editor';
    else if (tag === 'textarea') kind = 'textarea';
    else if (tag === 'input') kind = `input[${el.type || '?'}]`;
    else if (el.isContentEditable) kind = 'editable';
    const tile = tileOf(el);
    const where = tile ? `list=${String(tile.getAttribute('data-list-id') || '').slice(0, 6)}` : 'off-tile';
    const flags = [];
    if (el.readOnly) flags.push('readonly');
    if (el.disabled) flags.push('disabled');
    // A CodeMirror editor that has been put into read-only mode renders its
    // content as contenteditable=false, which cannot hold a caret at all —
    // the state the edit lock produces. Worth knowing on every entry.
    try {
      const content = el.closest && el.closest('.cm-editor')
        ? el.closest('.cm-editor').querySelector('.cm-content')
        : null;
      if (content && content.getAttribute('contenteditable') === 'false') flags.push('cm-readonly');
    } catch { /* ignore */ }
    return `${kind}(${where})${flags.length ? ' ' + flags.join(',') : ''}`;
  } catch { return 'unreadable'; }
}

/** Does clicking this thing mean "put the caret here"? */
function takesCaret(el) {
  if (!el) return false;
  try {
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'textarea') return true;
    if (tag === 'input') return !/^(button|submit|checkbox|radio|file|reset|image)$/.test(el.type || '');
    if (el.isContentEditable) return true;
    // Inside a CodeMirror editor counts even when it is currently read-only:
    // a click that does nothing BECAUSE the editor is locked is exactly one of
    // the things we want recorded.
    if (el.closest && el.closest('.cm-content')) return true;
    return false;
  } catch { return false; }
}

function note(entry) {
  try {
    const entries = read();
    entries.push({
      t: Date.now(),
      w: getWindowLabel(),
      vis: typeof document !== 'undefined' ? document.visibilityState : '?',
      win: typeof document !== 'undefined' && document.hasFocus ? document.hasFocus() : null,
      ...entry,
    });
    write(entries);
  } catch { /* ignore */ }
}

function onPointerDown(e) {
  lastPointerAt = Date.now();
  const target = e.target;
  if (!takesCaret(target)) return;
  const clicked = describe(target);
  const before = describe(document.activeElement);
  const tile = tileOf(target);
  setTimeout(() => {
    try {
      const now = document.activeElement;
      // Landed in the clicked element, or anywhere inside the same tile
      // (clicking the text and getting the tile's editor is a hit).
      if (now === target) return;
      if (tile && now && tile.contains(now)) return;
      note({
        kind: 'click-missed',
        clicked,
        before,
        after: describe(now),
        // Did the thing we clicked survive? A tile that unmounted between the
        // click and this check explains a caret that never arrives — and the
        // canvas drops an ARCHIVED tile the moment another tile takes focus.
        gone: !(target.isConnected && (!tile || tile.isConnected)),
        prevented: !!e.defaultPrevented,
      });
    } catch { /* ignore */ }
  }, SETTLE_MS);
}

function onFocusIn(e) {
  try {
    const now = Date.now();
    if (now - lastPointerAt < UNPROMPTED_MS) return;  // a click put it there
    if (now - lastKeyAt < UNPROMPTED_MS) return;      // Tab, or typing
    const to = e.target;
    const from = e.relatedTarget || null;
    // Only interesting when it moves AWAY from a caret, or INTO one unasked.
    if (!takesCaret(to) && !takesCaret(from)) return;
    note({ kind: 'focus-stolen', from: describe(from), to: describe(to) });
  } catch { /* ignore */ }
}

function onKeyDown() {
  lastKeyAt = Date.now();
}

/**
 * Install the two listeners. Idempotent, and safe to call before React mounts
 * — both listeners are on the document and survive every re-render.
 */
export function startFocusTrail() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  // Capture phase: we want to see the click before anything can stop it.
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('focusin', onFocusIn, true);
  document.addEventListener('keydown', onKeyDown, true);
}

export function readFocusTrail() {
  return read();
}

export function clearFocusTrail() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

/** Paste-ready. Newest LAST, so it reads chronologically like the other logs. */
export function formatFocusTrail() {
  const entries = read();
  if (!entries.length) {
    return 'focus trail is empty — every click that asked for a caret on this device got one, '
      + 'and nothing has taken focus away on its own. (That is the good outcome.)';
  }
  const out = [
    `listicity focus trail (${entries.length} events, oldest first)`,
    'click-missed = a click on something that takes a caret, where focus ended up elsewhere.',
    'focus-stolen = focus moved with no click and no key press behind it.',
    'Element kinds, list ids and flags only — never the text of a list.',
    '',
  ];
  for (const e of entries) {
    const when = new Date(e.t).toISOString().replace('T', ' ').slice(0, 19);
    const env = `w=${e.w || '?'} vis=${e.vis || '?'} winfocus=${e.win === null ? '?' : e.win}`;
    if (e.kind === 'click-missed') {
      out.push(`──── ${when}  CLICK MISSED  ${env}`);
      out.push(`  clicked : ${e.clicked}`);
      out.push(`  focus was: ${e.before}`);
      out.push(`  focus went: ${e.after}`);
      out.push(`  clicked target gone: ${e.gone}   click prevented: ${e.prevented}`);
    } else {
      out.push(`──── ${when}  FOCUS STOLEN  ${env}`);
      out.push(`  from: ${e.from}`);
      out.push(`  to  : ${e.to}`);
    }
    out.push('');
  }
  return out.join('\n');
}
